import React, { useEffect, useState, useCallback } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { getSuperAdminAudit, getSuperAdminAuditActions } from '../lib/api';
import { formatDate } from '../lib/utils';
import { toast } from 'sonner';
import {
  History, Shield, Eye, Building2, Database,
  Filter, Loader2, ChevronLeft, ChevronRight, Search, X,
} from 'lucide-react';

const CATEGORY_META = {
  security:      { label: 'Seguridad',      color: 'bg-red-500/20 text-red-400 border-red-500/30',        Icon: Shield },
  view_as:       { label: 'Vista como',     color: 'bg-amber-500/20 text-amber-400 border-amber-500/30',  Icon: Eye },
  organizations: { label: 'Organizaciones', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30',     Icon: Building2 },
  data:          { label: 'Datos',          color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', Icon: Database },
  other:         { label: 'Otros',          color: 'bg-muted text-muted-foreground border-border',        Icon: History },
};

const ACTION_LABELS = {
  account_locked: 'Cuenta bloqueada',
  account_unlocked: 'Cuenta desbloqueada',
  password_reset_requested: 'Reset contraseña solicitado',
  password_reset_completed: 'Reset contraseña completado',
  master_admin_transferred: 'Master Admin transferido',
  super_admin_granted: 'Super Admin otorgado',
  super_admin_revoked: 'Super Admin revocado',
  view_as_started: 'Vista-como iniciada',
  view_as_toggle_edit: 'Vista-como edición activada',
  view_as_exited: 'Vista-como terminada',
  org_created: 'Organización creada',
  org_updated: 'Organización actualizada',
  org_deactivated: 'Organización desactivada',
  org_reactivated: 'Organización reactivada',
  user_invited: 'Usuario invitado',
  user_removed: 'Usuario removido',
  user_created: 'Usuario creado',
  user_updated: 'Usuario actualizado',
  auto_sync: 'Sincronización automática',
  manual_sync: 'Sincronización manual',
  bulk_import: 'Importación masiva',
  shipment_imported: 'Embarque importado',
  shipment_created: 'Embarque creado',
  shipment_deleted: 'Embarque eliminado',
};

const PAGE_SIZE = 50;

export default function AuditLogsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({
    category: 'all', action: 'all', user_email: '',
    entity_type: '', date_from: '', date_to: '',
  });
  const [actionsByCategory, setActionsByCategory] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, skip: page * PAGE_SIZE };
      if (filters.category !== 'all') params.category = filters.category;
      if (filters.action !== 'all') params.action = filters.action;
      if (filters.user_email) params.user_email = filters.user_email;
      if (filters.entity_type) params.entity_type = filters.entity_type;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const res = await getSuperAdminAudit(params);
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setCounts(res.data.counts_30d || {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al cargar logs');
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    getSuperAdminAuditActions()
      .then((r) => setActionsByCategory(r.data.categories || {}))
      .catch(() => {});
  }, []);

  const resetFilters = () => {
    setPage(0);
    setFilters({ category: 'all', action: 'all', user_email: '', entity_type: '', date_from: '', date_to: '' });
  };

  const updateFilter = (key, value) => {
    setPage(0);
    setFilters((prev) => ({ ...prev, [key]: value, ...(key === 'category' ? { action: 'all' } : {}) }));
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const availableActions = filters.category !== 'all' ? (actionsByCategory[filters.category] || []) : [];

  if (!user?.is_super_admin && !user?.is_master_admin) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto py-12 text-center">
          <Shield size={48} className="mx-auto text-red-400 mb-4" />
          <h2 className="font-heading text-2xl mb-2">Acceso restringido</h2>
          <p className="text-muted-foreground">Esta sección está disponible sólo para Super Admins.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto space-y-6 py-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl flex items-center gap-2">
              <History size={28} /> Audit Logs
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Historial de acciones críticas del sistema (últimos 30 días mostrados en el resumen)
            </p>
          </div>
          <Badge variant="outline" className="text-sm" data-testid="audit-total-count">
            {total.toLocaleString()} eventos totales
          </Badge>
        </div>

        {/* Category summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(CATEGORY_META).filter(([k]) => k !== 'other').map(([key, meta]) => {
            const Icon = meta.Icon;
            const n = counts[key] ?? 0;
            return (
              <div
                key={key}
                className={`border rounded-lg p-4 cursor-pointer transition-all hover:border-primary ${filters.category === key ? 'border-primary bg-primary/5' : ''}`}
                onClick={() => updateFilter('category', filters.category === key ? 'all' : key)}
                data-testid={`audit-category-${key}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className={`inline-flex items-center gap-2 px-2 py-1 rounded border ${meta.color} text-xs`}>
                      <Icon size={12} /> {meta.label}
                    </div>
                    <p className="text-2xl font-heading mt-2">{n}</p>
                    <p className="text-xs text-muted-foreground">últimos 30 días</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Filters */}
        <div className="border rounded-lg p-4 bg-card">
          <div className="flex items-center gap-2 mb-3">
            <Filter size={16} /> <span className="font-medium">Filtros</span>
            {(filters.user_email || filters.entity_type || filters.date_from || filters.date_to || filters.category !== 'all' || filters.action !== 'all') && (
              <Button variant="ghost" size="sm" onClick={resetFilters} data-testid="audit-reset-filters-btn">
                <X size={14} className="mr-1" /> Limpiar
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div>
              <Label className="text-xs">Categoría</Label>
              <Select value={filters.category} onValueChange={(v) => updateFilter('category', v)}>
                <SelectTrigger data-testid="audit-filter-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {Object.entries(CATEGORY_META).filter(([k]) => k !== 'other').map(([k, m]) => (
                    <SelectItem key={k} value={k}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Acción</Label>
              <Select value={filters.action} onValueChange={(v) => updateFilter('action', v)} disabled={filters.category === 'all'}>
                <SelectTrigger data-testid="audit-filter-action"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {availableActions.map((a) => (
                    <SelectItem key={a} value={a}>{ACTION_LABELS[a] || a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Usuario (email)</Label>
              <Input
                placeholder="parte del email"
                value={filters.user_email}
                onChange={(e) => updateFilter('user_email', e.target.value)}
                data-testid="audit-filter-user-email"
              />
            </div>
            <div>
              <Label className="text-xs">Entidad</Label>
              <Input
                placeholder="shipment, container..."
                value={filters.entity_type}
                onChange={(e) => updateFilter('entity_type', e.target.value)}
                data-testid="audit-filter-entity-type"
              />
            </div>
            <div>
              <Label className="text-xs">Desde</Label>
              <Input type="date" value={filters.date_from} onChange={(e) => updateFilter('date_from', e.target.value)} data-testid="audit-filter-date-from" />
            </div>
            <div>
              <Label className="text-xs">Hasta</Label>
              <Input type="date" value={filters.date_to} onChange={(e) => updateFilter('date_to', e.target.value)} data-testid="audit-filter-date-to" />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="border rounded-lg overflow-hidden bg-card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="audit-table">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Fecha</th>
                  <th className="text-left px-4 py-3 font-medium">Categoría</th>
                  <th className="text-left px-4 py-3 font-medium">Acción</th>
                  <th className="text-left px-4 py-3 font-medium">Usuario</th>
                  <th className="text-left px-4 py-3 font-medium">Entidad</th>
                  <th className="text-left px-4 py-3 font-medium">Descripción</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="text-center py-16"><Loader2 className="animate-spin inline" /> Cargando...</td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-16 text-muted-foreground">
                    <Search size={32} className="mx-auto mb-2 opacity-50" />
                    No hay eventos que coincidan
                  </td></tr>
                ) : items.map((item, i) => {
                  const meta = CATEGORY_META[item.category] || CATEGORY_META.other;
                  const Icon = meta.Icon;
                  return (
                    <tr key={item.id || i} className="border-t hover:bg-muted/30 transition-colors" data-testid={`audit-row-${i}`}>
                      <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                        {formatDate(item.created_at, true)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded border text-xs ${meta.color}`}>
                          <Icon size={12} /> {meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">{ACTION_LABELS[item.action] || item.action}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {item.user_name || item.user_email || '—'}
                        {item.user_email && item.user_name && (
                          <div className="text-xs text-muted-foreground">{item.user_email}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {item.entity_type && (
                          <span className="font-mono text-xs">{item.entity_type}:{(item.entity_id || '').slice(0, 12)}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 max-w-md">
                        <div className="truncate" title={item.description}>{item.description}</div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t bg-muted/20">
            <span className="text-xs text-muted-foreground">
              Página {page + 1} de {totalPages} · {items.length} de {total.toLocaleString()}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page === 0 || loading} onClick={() => setPage((p) => Math.max(0, p - 1))} data-testid="audit-prev-btn">
                <ChevronLeft size={14} /> Anterior
              </Button>
              <Button variant="outline" size="sm" disabled={page + 1 >= totalPages || loading} onClick={() => setPage((p) => p + 1)} data-testid="audit-next-btn">
                Siguiente <ChevronRight size={14} />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
